import { useState } from "react";
import type { RecordData } from "./Workflow";
import "./PortalNotifications.css";

const notificationOptions = [
  ["notify_missed_bookings", "Missed bookings", "missed_booking"],
  ["notify_online_bookings", "Online bookings", "online_booking"],
  ["notify_cancellations", "Cancellations", "cancellation"],
  ["notify_lsa_digest", "Call scoring summary", "lsa_call"],
];

/** The form owns its mode so leaving Settings cannot keep an unsaved selection. */
export default function PortalNotifications({ data, busy, onSave }: {
  data: RecordData;
  busy: boolean;
  onSave: (fields: RecordData) => Promise<unknown>;
}) {
  const [mode, setMode] = useState(data.notification_mode || "live");
  const [callbackOn, setCallbackOn] = useState(data.callback_email_enabled === true);
  const callbackAvailable = data.callback_email_available !== false;
  const callbackEmails = Array.isArray(data.callback_emails) ? (data.callback_emails as string[]).join(", ") : "";
  const savedMode = data.notification_mode || 'live';
  const emailAvailable = data.delivery_channels?.email === true;
  const dailyDigestAvailable = data.delivery_channels?.daily_digest === true;
  const eventAvailable = (eventType: string) => Array.isArray(data.supported_event_types) && data.supported_event_types.includes(eventType);
  const availableOptions = notificationOptions.filter(([, , eventType]) => eventAvailable(eventType));
  const unavailableOptions = notificationOptions.filter(([, , eventType]) => !eventAvailable(eventType));
  function option([name, title, eventType]: string[]) {
    const available = eventAvailable(eventType);
    return <label className="checkbox-line" key={name}>
      <input type="checkbox" name={name} defaultChecked={data[name] !== false} disabled={!available || busy} aria-describedby={!available ? 'notification-events-status' : undefined} />
      <span>{title}{!available && ' (unavailable)'}</span>
    </label>;
  }
  return (
    <form className="panel workflow-panel operation-form portal-notifications" aria-busy={busy} onSubmit={async (event) => {
      event.preventDefault();
      if (busy) return;
      const form = new FormData(event.currentTarget);
      const fields: RecordData = Object.fromEntries(form);
      // A saved, unavailable option stays selected but native FormData omits
      // disabled options. Preserve it until the client chooses another mode.
      fields.notification_mode = mode;
      for (const [name, , eventType] of notificationOptions) {
        if (eventAvailable(eventType)) fields[name] = form.get(name) === "on";
        else delete fields[name]; // Preserve stored choices for disconnected sources.
      }
      if (emailAvailable) fields.client_email = String(form.get("client_email") || "").trim();
      fields.owner_high_recovery_enabled = form.get("owner_high_recovery_enabled") === "on";
      if (callbackAvailable) {
        fields.callback_email_enabled = callbackOn;
        fields.callback_emails = String(form.get("callback_emails") || "");
        fields.callback_level = String(form.get("callback_level") || "HIGH");
      }
      fields.digest_hour = Number(form.get("digest_hour"));
      await onSave(fields);
    }}>
      <h2>Workspace updates</h2>
      <p className="workflow-note">These preferences apply to everyone in this workspace. Your sign-in email and password-reset emails stay with your account.</p>
      <div className="portal-delivery-status" role="status">
        <p><strong>Email</strong>{emailAvailable ? (savedMode === 'silenced' ? 'Email alerts are paused.' : savedMode === 'daily_digest' && !dailyDigestAvailable ? 'Email is connected, but daily summaries are unavailable.' : String(data.client_email || '').trim() ? 'Email alerts are enabled for the saved address.' : 'Add an email address below to receive alerts.') : data.delivery_channels?.email_status === 'email_sender_unconfigured' ? 'Email delivery needs attention. Contact your agency.' : 'Email alerts are not enabled. Contact your agency to enable them.'}</p>
        <p><strong>Slack</strong>{!data.delivery_channels?.slack ? 'Slack delivery is not connected.' : data.delivery_channels.slack_status === 'delivery_failed' ? data.delivery_channels.slack_error === 'missing_scope' ? 'Slack needs permission to post alerts. Contact your agency.' : 'The last Slack delivery failed. Contact your agency.' : data.delivery_channels.slack_status === 'last_delivery_accepted' ? 'Slack accepted the last alert.' : 'Slack delivery is configured; delivery has not been verified.'}</p>
      </div>
      {emailAvailable ? <label>
        Notification email
        <input name="client_email" type="email" autoComplete="email" maxLength={254} defaultValue={data.client_email || ""} />
        <small className="field-hint">This does not change anyone’s sign-in email. Leave blank to stop email alerts.</small>
      </label> : data.client_email && <label>
        Saved notification email
        <input type="email" value={data.client_email} readOnly aria-describedby="notification-email-status" />
        <small id="notification-email-status" className="field-hint">This address is saved, but no alerts are sent by email.</small>
      </label>}
      <label>
        Delivery mode
        <select name="notification_mode" value={mode} aria-describedby={!dailyDigestAvailable ? 'notification-digest-status' : undefined} onChange={(event) => {
          if (event.target.value !== "daily_digest" || dailyDigestAvailable) setMode(event.target.value);
        }}>
          <option value="live">Send each update</option>
          <option value="daily_digest" disabled={!dailyDigestAvailable}>Daily summary</option>
          <option value="silenced">Paused</option>
        </select>
      </label>
      {mode !== savedMode && <p className="field-hint">Save preferences to apply the new delivery mode.</p>}
      {!dailyDigestAvailable && <p id="notification-digest-status" className="inline-notice" role="status">Daily summaries are unavailable. {mode === "daily_digest" ? 'Your saved Daily summary preference is still selected, but summaries will not be sent. ' : ''}Choose Send each update for future updates, or Paused to stop client updates.</p>}
      {mode === "silenced" && <p className="workflow-note">{savedMode === 'silenced' ? 'Client updates and daily summaries are paused.' : 'Saving Paused will pause client updates and daily summaries.'} Your agency can still receive internal alerts.</p>}
      {/* Keep the saved hour in the form while another mode is selected. */}
      <label hidden={mode !== "daily_digest" || !dailyDigestAvailable}>
        Daily summary time (Eastern)
        <select name="digest_hour" defaultValue={String(data.digest_hour ?? 8)}>
          {Array.from({ length: 24 }, (_, hour) => (
            <option key={hour} value={hour}>{`${hour % 12 || 12} ${hour < 12 ? "AM" : "PM"} ET`}</option>
          ))}
        </select>
      </label>
      {/* The hint belongs to the Missed bookings box, so it sits with the updates, not under Delivery mode. */}
      {availableOptions.length > 0 && <fieldset className="portal-notification-events"><legend>Updates to receive</legend>{availableOptions.map(option)}
        {eventAvailable('missed_booking') && <p className="field-hint">Missed booking alerts come from Google Local Services call reviews.</p>}</fieldset>}
      {unavailableOptions.length > 0 && <details className="portal-unavailable-updates">
        <summary>{unavailableOptions.length} more update {unavailableOptions.length === 1 ? "type" : "types"} (not available yet)</summary>
        <p id="notification-events-status" className="field-hint">These are not connected yet. Your saved choices are kept.</p>
        <div className="portal-notification-events">{unavailableOptions.map(option)}</div>
      </details>}
      <fieldset className="portal-notification-events portal-callback"><legend>Call-back emails</legend>
        <label className="checkbox-line"><input type="checkbox" checked={callbackOn} onChange={(e) => setCallbackOn(e.target.checked)} disabled={busy || !callbackAvailable} /><span>Send call-back emails when a Local Services Ads call can still be saved</span></label>
        {/* "Email is not set up, contact your agency" is said once, in the Email line at the top. */}
        <p className="field-hint">Within about 5 minutes of a call that did not book but likely will on a quick call back: the caller's name, number and what happened. Applies to calls from now on.{callbackAvailable ? "" : " Available once email is set up."}</p>
        <label>
          Send to
          <input name="callback_emails" type="text" inputMode="email" autoComplete="email" maxLength={1300} defaultValue={callbackEmails} disabled={busy || !callbackAvailable} placeholder="owner@yourbusiness.com" />
          <small className="field-hint">Up to 5 addresses, separated by commas.</small>
        </label>
        <label>
          Which calls
          <select name="callback_level" defaultValue={data.callback_level === "MEDIUM" ? "MEDIUM" : "HIGH"} disabled={busy || !callbackAvailable}>
            <option value="HIGH">High chance to book</option>
            <option value="MEDIUM">High or medium chance to book</option>
          </select>
        </label>
      </fieldset>
      <fieldset className="portal-notification-events"><legend>On your Overview</legend>
        <label className="checkbox-line"><input type="checkbox" name="owner_high_recovery_enabled" defaultChecked={data.owner_high_recovery_enabled === true} disabled={busy} /><span>Show calls worth calling back</span></label>
        <p className="field-hint">New calls likely to book on a quick call back show on your Overview. Starts with calls after this is on. Sends no email, text or Slack message.</p>
      </fieldset>
      <button className="button" disabled={busy}>{busy ? "Saving…" : "Save preferences"}</button>
    </form>
  );
}
